<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TicketsAPI from 'dashboard/api/tickets';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();

const tickets = ref([]);
const isLoading = ref(true);
const error = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const PER_PAGE = 25;

const statusConfig = {
  open:             { label: 'TICKETS.STATUS.OPEN',             class: 'bg-n-brand/10 text-n-blue-text' },
  in_progress:      { label: 'TICKETS.STATUS.IN_PROGRESS',      class: 'bg-n-amber-9/10 text-n-amber-11' },
  escalated:        { label: 'TICKETS.STATUS.ESCALATED',        class: 'bg-n-ruby-9/10 text-n-ruby-11' },
  waiting_client:   { label: 'TICKETS.STATUS.WAITING_CLIENT',   class: 'bg-n-amber-9/20 text-n-amber-11' },
  waiting_internal: { label: 'TICKETS.STATUS.WAITING_INTERNAL', class: 'bg-n-amber-9/20 text-n-amber-11' },
  resolved:         { label: 'TICKETS.STATUS.RESOLVED',         class: 'bg-n-teal-9/10 text-n-teal-11' },
  closed:           { label: 'TICKETS.STATUS.CLOSED',           class: 'bg-n-slate-9/10 text-n-slate-11' },
  cancelled:        { label: 'TICKETS.STATUS.CANCELLED',        class: 'bg-n-slate-9/10 text-n-slate-11' },
};

const priorityConfig = {
  urgent: { label: 'TICKETS.PRIORITY.URGENT', class: 'bg-n-ruby-9/20 text-n-ruby-11' },
  high:   { label: 'TICKETS.PRIORITY.HIGH',   class: 'bg-n-amber-9/20 text-n-amber-11' },
  medium: { label: 'TICKETS.PRIORITY.MEDIUM', class: 'bg-n-amber-9/10 text-n-amber-11' },
  low:    { label: 'TICKETS.PRIORITY.LOW',    class: 'bg-n-teal-9/20 text-n-teal-11' },
};

const statusOf = ticket =>
  statusConfig[ticket.status] || statusConfig.open;

const priorityOf = ticket =>
  priorityConfig[ticket.priority] || priorityConfig.low;

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const openTicket = ticket => {
  router.push({ name: 'ticket_details', params: { ticketId: ticket.display_id } });
};

const fetchTickets = async (page = 1) => {
  isLoading.value = true;
  error.value = null;
  try {
    const res = await TicketsAPI.get({
      contract_number: props.numeroContrato,
      sort: 'created_at_desc',
      page,
      per_page: PER_PAGE,
    });
    tickets.value  = res.data?.payload ?? [];
    currentPage.value = res.data?.meta?.current_page ?? page;
    totalPages.value  = res.data?.meta?.total_pages ?? 1;
    totalCount.value  = res.data?.meta?.count ?? tickets.value.length;
  } catch (err) {
    error.value = err.message || 'Error al cargar tickets';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchTickets();
});
</script>

<template>
  <div class="space-y-4">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">{{ $t('CONTRACTS.TICKETS.LOADING') }}</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex flex-col items-center justify-center py-12 gap-4">
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">{{ error }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="tickets.length === 0" class="flex flex-col items-center justify-center py-12 gap-3">
      <span class="i-lucide-ticket text-6xl text-n-slate-10" />
      <p class="text-n-slate-11">{{ $t('CONTRACTS.TICKETS.NOT_FOUND') }}</p>
    </div>

    <!-- Tickets list -->
    <div v-else class="space-y-4">
      <!-- Header -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 flex items-center gap-2">
          <span class="i-lucide-ticket" />
          Tickets del Contrato
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ $t('CONTRACTS.TICKETS.FOUND_COUNT', { count: totalCount }) }}
        </p>
      </div>

      <!-- Table -->
      <div class="bg-n-background border border-n-weak rounded-lg overflow-hidden">
        <table class="w-full">
          <thead class="bg-n-solid-3 border-b border-n-weak">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Folio</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Título</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase">Estado</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase">Prioridad</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Asignado</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Creado</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="ticket in tickets"
              :key="ticket.id"
              class="hover:bg-n-alpha-1 transition-colors cursor-pointer"
              @click="openTicket(ticket)"
            >
              <td class="px-4 py-3">
                <span class="font-mono text-sm text-n-slate-12">{{ ticket.folio }}</span>
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 max-w-[220px]">
                <span class="line-clamp-2">{{ ticket.title }}</span>
              </td>
              <td class="px-4 py-3 text-center">
                <span
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="statusOf(ticket).class"
                >
                  {{ $t(statusOf(ticket).label) }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <span
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="priorityOf(ticket).class"
                >
                  {{ $t(priorityOf(ticket).label) }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ ticket.assignee?.name || '-' }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
                {{ formatDate(ticket.created_at) }}
              </td>
              <td class="px-4 py-3 text-center">
                <span class="i-lucide-arrow-right text-n-slate-10 hover:text-n-slate-12" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="totalPages > 1" class="flex items-center justify-between px-2 py-3">
        <span class="text-sm text-n-slate-11">
          Página {{ currentPage }} de {{ totalPages }}
        </span>
        <div class="flex gap-2">
          <button
            class="px-3 py-1 text-sm rounded bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="currentPage === 1"
            @click="fetchTickets(currentPage - 1)"
          >
            ‹ Anterior
          </button>
          <button
            class="px-3 py-1 text-sm rounded bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="currentPage === totalPages"
            @click="fetchTickets(currentPage + 1)"
          >
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
